import type { ReploProject, ReploSimpleProject } from "./generated/project";

import { z } from "zod";

/**
 * A "slug" in our system is defined as a string between 1 and 256 characters containing
 * only alpha-numeric characters in addition to an underscore or hyphen.
 */
export function isPathSafeSlug(value: string) {
  return /^([\w-]){1,256}$/.test(value);
}

/**
 * Very general numeric string schema that accepts integers and floats as strings. We use this
 * in places where we either accept user input, or want to capture values as strings, but still
 * have some structure for them, like currencies in analytics.
 *
 * @author Ben 2024-12-04
 */
export const numericStringSchema = z
  .string()
  .transform<string>((amount, ctx) => {
    const isAmountNumeric = /[+-]?(\d*\.)?\d+/.test(amount);
    if (!isAmountNumeric) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "value must be numeric",
      });
    }
    return amount;
  })
  .pipe(z.string());

export const slugSchema = z
  .string()
  .min(1)
  .max(256)
  .refine(isPathSafeSlug, {
    error:
      "Slug must contain 1 and 256 characters, with only letters, numbers, underscore",
  })
  .describe("Slug");

/**
 * This converts a nullish (T | null | undefined) to an optional (T | undefined)
 */
export function nullishToOptional<SchemaType>(schema: z.ZodType<SchemaType>) {
  return z
    .preprocess((value) => value ?? undefined, schema.optional())
    .describe("nullishToOptional");
}

// Note (Evan, 2024-01-29): Given the name of an env variable and a zod schema to represent
// what type it should be, parses the variable from process.env and returns if it has the
// correct type, otherwise throwing an error.
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function parseEnvVariable<SchemaType extends z.ZodType<any, any>>(
  variableName: string,
  schema: SchemaType,
): z.infer<SchemaType> {
  const value = process.env[variableName];
  const parseResult = schema.safeParse(value);

  if (parseResult.success) {
    return parseResult.data;
  }

  // eslint-disable-next-line replo/no-raw-error-throw
  throw new Error(
    `env var ${variableName} is required to be set, got ${value}`,
  );
}

export const numberAsStringSchema = z.preprocess((value) => {
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return value;
}, z.number());

export const booleanAsStringDefaultSchema = z.preprocess((value) => {
  if (value === "true") {
    return true;
  }
  return false;
}, z.boolean());

/**
 * This util processes the url params that should be arrays into an actual array.
 * In the url an array is represented as multiple params with the same name, but we could send an
 * array of one element. This will be only one param and Fastify will recognize that as a string and
 * not as an array, so we need to convert it to an array to have the correct type in the services.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function processArrayUrlParam<SchemaType extends z.ZodType<any>>(
  arraySchema: SchemaType,
  defaultValue: z.infer<z.ZodArray<SchemaType>>,
) {
  return z
    .preprocess((value) => {
      return typeof value === "string" ? [value] : value;
    }, z.array(arraySchema))
    .prefault(defaultValue);
}

export function isShopifyIntegrationEnabled(
  project?: ReploProject | ReploSimpleProject | null,
) {
  return Boolean(project?.integrations?.shopify?.store);
}

/**
 * Like z.boolean(), but also accepts "true" and "false" as strings and
 * converts them to true / false.
 *
 * Note (Noah, 2025-01-07): This is not the default behavior of z.coerce.boolean(),
 * because that uses the Boolean() constructor which actually returns true
 * for the string "false"
 */
export const coerceStringToBooleanSchema = z.preprocess((value) => {
  if (typeof value === "string") {
    if (value === "true") {
      return true;
    }
    if (value === "false") {
      return false;
    }
  }
  return value;
}, z.boolean());
